/**
 * Track Record Store
 *
 * Manages track record state for challenge documentation and verification.
 * A track record is the user's evidence of completing a challenge, consisting
 * of entries (text + media) and optional milestone checkpoints. Supports the
 * full lifecycle: create, add entries, attach media, submit for verification,
 * and share. Also includes teacher review actions for approving or rejecting
 * student track records with templated rejection reasons.
 *
 * @module useTrackRecordStore
 *
 * @example
 * ```tsx
 * import { useTrackRecordStore, selectTrackRecord, selectEntries } from '@/store/useTrackRecordStore'
 *
 * function TrackRecordEditor({ challengeId }: { challengeId: string }) {
 *   const { fetchTrackRecord, addEntry, submitForVerification } = useTrackRecordStore()
 *   const record = useTrackRecordStore(selectTrackRecord)
 *   const entries = useTrackRecordStore(selectEntries)
 *
 *   useEffect(() => { fetchTrackRecord(challengeId) }, [challengeId])
 *
 *   return <EntryList entries={entries} onAddEntry={addEntry} />
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type { TrackRecord, TrackRecordEntry, TrackRecordComment, PendingReview, StudentTrackRecordSummary, TrackRecordFeedEntry, RejectionReasonTemplate, EntryReactionEmoji } from '../types'
import { trackRecordApi } from '../api'

interface TrackRecordState {
  // Current track record
  trackRecord: TrackRecord | null
  entries: TrackRecordEntry[]

  // Comments (keyed by entry ID)
  commentsByEntry: Record<string, TrackRecordComment[]>
  isLoadingComments: boolean

  // Teacher review state
  pendingReviews: PendingReview[]
  communityTrackRecords: StudentTrackRecordSummary[]
  trackRecordFeed: TrackRecordFeedEntry[]
  reviewTrackRecord: TrackRecord | null
  rejectionReasonTemplates: RejectionReasonTemplate[]
  isLoadingReviews: boolean

  // Loading states
  isLoading: boolean
  isSaving: boolean

  // Error state
  error: string | null

  // Actions
  fetchTrackRecord: (challengeId: string) => Promise<void>
  createTrackRecord: (challengeId: string) => Promise<boolean>
  addEntry: (data: { textContent?: string; milestoneId?: string }) => Promise<boolean>
  updateEntry: (entryId: string, data: { textContent?: string }) => Promise<boolean>
  deleteEntry: (entryId: string) => Promise<boolean>
  addMedia: (entryId: string, data: {
    mediaType: 'image' | 'video_link' | 'audio_link' | 'document'
    url: string
    fileSizeBytes?: number
    originalFilename?: string
  }) => Promise<boolean>
  submitForVerification: () => Promise<boolean>
  updateSharingScope: (scope: 'private' | 'communities' | 'individuals' | 'public') => Promise<boolean>

  // Comment actions
  fetchComments: (entryId: string) => Promise<void>
  addComment: (entryId: string, content: string, parentCommentId?: string) => Promise<boolean>
  updateComment: (commentId: string, entryId: string, content: string) => Promise<boolean>
  deleteComment: (commentId: string, entryId: string) => Promise<boolean>

  // Teacher review actions
  fetchCommunityTrackRecords: (communityId: string) => Promise<void>
  fetchTrackRecordFeed: (communityId: string) => Promise<void>
  fetchPendingReviews: (communityId?: string) => Promise<void>
  fetchTrackRecordForReview: (trackRecordId: string) => Promise<void>
  fetchRejectionReasonTemplates: () => Promise<void>
  verifyTrackRecord: (trackRecordId: string, note?: string) => Promise<boolean>
  rejectTrackRecord: (trackRecordId: string, reasonId: string, note?: string) => Promise<boolean>

  // Reaction actions
  toggleReaction: (entryId: string, emoji: EntryReactionEmoji) => Promise<void>

  clearError: () => void
  reset: () => void
}

const initialState = {
  trackRecord: null,
  entries: [],
  commentsByEntry: {} as Record<string, TrackRecordComment[]>,
  isLoadingComments: false,
  pendingReviews: [] as PendingReview[],
  communityTrackRecords: [] as StudentTrackRecordSummary[],
  trackRecordFeed: [] as TrackRecordFeedEntry[],
  reviewTrackRecord: null as TrackRecord | null,
  rejectionReasonTemplates: [] as RejectionReasonTemplate[],
  isLoadingReviews: false,
  isLoading: false,
  isSaving: false,
  error: null,
}

export const useTrackRecordStore = createWithEqualityFn<TrackRecordState>()((set, get) => ({
  ...initialState,

  fetchTrackRecord: async (challengeId) => {
    set({ isLoading: true, error: null })

    const response = await trackRecordApi.getTrackRecord(challengeId)

    if (response.success) {
      set({
        trackRecord: response.data,
        entries: response.data?.entries ?? [],
        isLoading: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to load track record',
        isLoading: false,
      })
    }
  },

  createTrackRecord: async (challengeId) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.createTrackRecord(challengeId)

    if (response.success && response.data) {
      set({
        trackRecord: response.data,
        entries: response.data.entries ?? [],
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to create track record',
        isSaving: false,
      })
      return false
    }
  },

  addEntry: async (data) => {
    const { trackRecord } = get()
    if (!trackRecord) {
      set({ error: 'No track record found' })
      return false
    }

    set({ isSaving: true, error: null })

    const response = await trackRecordApi.addEntry(trackRecord.id, data)

    if (response.success && response.data) {
      set({
        entries: [...get().entries, response.data],
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to add entry',
        isSaving: false,
      })
      return false
    }
  },

  updateEntry: async (entryId, data) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.updateEntry(entryId, data)

    if (response.success && response.data) {
      set({
        entries: get().entries.map((e) =>
          e.id === entryId ? { ...e, ...response.data } : e
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update entry',
        isSaving: false,
      })
      return false
    }
  },

  deleteEntry: async (entryId) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.deleteEntry(entryId)

    if (response.success) {
      set({
        entries: get().entries.filter((e) => e.id !== entryId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to delete entry',
        isSaving: false,
      })
      return false
    }
  },

  addMedia: async (entryId, data) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.addMedia(entryId, data)

    if (response.success && response.data) {
      // Update entry with new media
      set({
        entries: get().entries.map((e) => {
          if (e.id === entryId) {
            return {
              ...e,
              media: [...(e.media ?? []), response.data!],
            }
          }
          return e
        }),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to add media',
        isSaving: false,
      })
      return false
    }
  },

  submitForVerification: async () => {
    const { trackRecord } = get()
    if (!trackRecord) {
      set({ error: 'No track record found' })
      return false
    }

    set({ isSaving: true, error: null })

    const response = await trackRecordApi.submitForVerification(trackRecord.id)

    if (response.success && response.data) {
      set({
        trackRecord: response.data,
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to submit for verification',
        isSaving: false,
      })
      return false
    }
  },

  updateSharingScope: async (scope) => {
    const { trackRecord } = get()
    if (!trackRecord) {
      set({ error: 'No track record found' })
      return false
    }

    set({ isSaving: true, error: null })

    const response = await trackRecordApi.updateSharingScope(trackRecord.id, scope)

    if (response.success && response.data) {
      set({
        trackRecord: response.data,
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update sharing settings',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Comment Actions
  // ============================================================================

  fetchComments: async (entryId) => {
    set({ isLoadingComments: true })

    const response = await trackRecordApi.getEntryComments(entryId)

    if (response.success && response.data) {
      set({
        commentsByEntry: {
          ...get().commentsByEntry,
          [entryId]: response.data,
        },
        isLoadingComments: false,
      })
    } else {
      set({ isLoadingComments: false })
    }
  },

  addComment: async (entryId, content, parentCommentId) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.addComment(entryId, { content, parentCommentId })

    if (response.success && response.data) {
      const currentComments = get().commentsByEntry[entryId] ?? []
      set({
        commentsByEntry: {
          ...get().commentsByEntry,
          [entryId]: [response.data, ...currentComments],
        },
        entries: get().entries.map((e) =>
          e.id === entryId ? { ...e, commentCount: e.commentCount + 1 } : e
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to add comment',
        isSaving: false,
      })
      return false
    }
  },

  updateComment: async (commentId, entryId, content) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.updateComment(commentId, { content })

    if (response.success && response.data) {
      const currentComments = get().commentsByEntry[entryId] ?? []
      set({
        commentsByEntry: {
          ...get().commentsByEntry,
          [entryId]: currentComments.map((c) =>
            c.id === commentId ? response.data! : c
          ),
        },
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update comment',
        isSaving: false,
      })
      return false
    }
  },

  deleteComment: async (commentId, entryId) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.deleteComment(commentId)

    if (response.success) {
      const currentComments = get().commentsByEntry[entryId] ?? []
      set({
        commentsByEntry: {
          ...get().commentsByEntry,
          [entryId]: currentComments.filter((c) => c.id !== commentId),
        },
        entries: get().entries.map((e) =>
          e.id === entryId ? { ...e, commentCount: Math.max(0, e.commentCount - 1) } : e
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to delete comment',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Teacher Review Actions
  // ============================================================================

  fetchCommunityTrackRecords: async (communityId) => {
    set({ isLoadingReviews: true, error: null })

    const response = await trackRecordApi.getCommunityStudentTrackRecords(communityId)

    if (response.success && response.data) {
      set({
        communityTrackRecords: response.data,
        isLoadingReviews: false,
      })
    } else {
      set({
        communityTrackRecords: [],
        isLoadingReviews: false,
      })
    }
  },

  fetchTrackRecordFeed: async (communityId) => {
    set({ isLoadingReviews: true, error: null })

    const response = await trackRecordApi.getCommunityTrackRecordFeed(communityId)

    if (response.success && response.data) {
      set({
        trackRecordFeed: response.data,
        isLoadingReviews: false,
      })
    } else {
      set({
        trackRecordFeed: [],
        isLoadingReviews: false,
      })
    }
  },

  fetchPendingReviews: async (communityId) => {
    set({ isLoadingReviews: true, error: null })

    const response = await trackRecordApi.getPendingReviewsForTeacher(communityId)

    if (response.success && response.data) {
      set({
        pendingReviews: response.data,
        isLoadingReviews: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to load pending reviews',
        isLoadingReviews: false,
      })
    }
  },

  fetchTrackRecordForReview: async (trackRecordId) => {
    set({ isLoading: true, error: null })

    const response = await trackRecordApi.getTrackRecordForReview(trackRecordId)

    if (response.success && response.data) {
      set({
        reviewTrackRecord: response.data,
        isLoading: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to load track record for review',
        isLoading: false,
      })
    }
  },

  fetchRejectionReasonTemplates: async () => {
    const response = await trackRecordApi.getRejectionReasonTemplates()

    if (response.success && response.data) {
      set({ rejectionReasonTemplates: response.data })
    }
  },

  verifyTrackRecord: async (trackRecordId, note) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.verifyTrackRecord(trackRecordId, note)

    if (response.success) {
      // Remove from pending reviews list
      set({
        pendingReviews: get().pendingReviews.filter((p) => p.trackRecordId !== trackRecordId),
        reviewTrackRecord: null,
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to verify track record',
        isSaving: false,
      })
      return false
    }
  },

  rejectTrackRecord: async (trackRecordId, reasonId, note) => {
    set({ isSaving: true, error: null })

    const response = await trackRecordApi.rejectTrackRecord(trackRecordId, {
      rejectionReasonId: reasonId,
      rejectionNote: note,
    })

    if (response.success) {
      // Remove from pending reviews list
      set({
        pendingReviews: get().pendingReviews.filter((p) => p.trackRecordId !== trackRecordId),
        reviewTrackRecord: null,
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to reject track record',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Reaction Actions
  // ============================================================================

  toggleReaction: async (entryId, emoji) => {
    const response = await trackRecordApi.toggleReaction(entryId, emoji)

    if (response.success && response.data) {
      const { reactions, userReactions } = response.data
      // Update the entry in the feed (teacher view)
      set({
        trackRecordFeed: get().trackRecordFeed.map((fe) =>
          fe.entry.id === entryId
            ? { ...fe, entry: { ...fe.entry, reactions, userReactions } }
            : fe
        ),
        // Also update entries array (student view)
        entries: get().entries.map((e) =>
          e.id === entryId
            ? { ...e, reactions, userReactions }
            : e
        ),
      })
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select the current track record being viewed or edited. */
export const selectTrackRecord = (state: TrackRecordState) => state.trackRecord

/** Select all entries (text + media) for the current track record. */
export const selectEntries = (state: TrackRecordState) => state.entries

/** Check if the current track record has been submitted for verification. */
export const selectIsSubmitted = (state: TrackRecordState) =>
  state.trackRecord?.status === 'submitted'

/** Check if the current track record has been verified/approved. */
export const selectIsVerified = (state: TrackRecordState) =>
  state.trackRecord?.status === 'verified'

// Teacher review selectors

/** Select pending track record reviews for the teacher's classes. */
export const selectPendingReviews = (state: TrackRecordState) => state.pendingReviews

/** Select the count of pending reviews (for badge/notification display). */
export const selectPendingReviewCount = (state: TrackRecordState) => state.pendingReviews.length

/** Select the track record currently being reviewed by a teacher. */
export const selectReviewTrackRecord = (state: TrackRecordState) => state.reviewTrackRecord

/** Select available rejection reason templates for the review workflow. */
export const selectRejectionReasonTemplates = (state: TrackRecordState) => state.rejectionReasonTemplates

/** Check if reviews are currently loading. */
export const selectIsLoadingReviews = (state: TrackRecordState) => state.isLoadingReviews
